import { useMutation, useQuery, useQueryClient } from 'react-query';

import { useWeb3Context } from 'src/context/web3';
import { IMarketplaceItemDetail } from 'src/interface/Marketplace';
import convertBigNumber from 'src/utils/func/convert-bignumber';
import { removeEmptyParams } from 'src/utils/func/format-query';
import { notify } from 'src/utils/notify';

import useWeb3Marketplace from './web3/useWeb3Marketplace';
import { useAPICaller } from './useAPICaller';

export const queryKeyMarketplaceItemList = 'queryKeyMarketplaceItemList';
export const queryKeyMarketplaceItem = 'queryKeyMarketplaceItem';
export const queryKeyMarketplaceItemWeb3Data = 'queryKeyMarketplaceItemWeb3Data';

export const useGetMarketplaceItemList = (params: any, enabled = true) => {
	const { fetchAPI } = useAPICaller();

	const payload = removeEmptyParams(params);

	return useQuery(
		[queryKeyMarketplaceItemList, payload],
		() => {
			return fetchAPI({
				endpoint: '/marketplaces',
				params: payload,
			});
		},
		{ keepPreviousData: true, refetchOnWindowFocus: false, enabled: !!enabled },
	);
};

export const useGetMarketplaceItem = ({
	enabled = true,
	...props
}: {
	gameSlug: string;
	itemType: string;
	itemId: string;
	enabled?: boolean;
}) => {
	const { fetchAPI } = useAPICaller();

	const { gameSlug, itemType, itemId } = props;

	return useQuery(
		[queryKeyMarketplaceItem, props],
		() => {
			if (!gameSlug) return;

			return fetchAPI({
				endpoint: `/games/slug/${gameSlug}/item-type/${itemType}/nftId/${itemId}`,
			});
		},
		{ keepPreviousData: true, refetchOnWindowFocus: false, enabled: !!enabled },
	);
};

export const useGetMarketplaceItemWeb3Data = (item: IMarketplaceItemDetail, enabled = true) => {
	const { canAccessWeb3 } = useWeb3Context();
	const { web3GetItemByMarketplaceId } = useWeb3Marketplace();

	return useQuery(
		[queryKeyMarketplaceItemWeb3Data, item],
		async () => {
			if (!canAccessWeb3) {
				return item;
			}

			let result: IMarketplaceItemDetail = item;

			const itemData = await web3GetItemByMarketplaceId(item.marketplace.web3_marketplace_id);

			result = {
				...result,
				...itemData,
			};

			return result;
		},
		{ keepPreviousData: true, refetchOnWindowFocus: false, enabled: !!enabled },
	);
};

export const useBuyMarketplaceItem = () => {
	const { fetchAPI } = useAPICaller();

	const { web3BuyItem } = useWeb3Marketplace();

	return useMutation(
		async ({ item, totalPrice }: { item: IMarketplaceItemDetail; totalPrice: number }) => {
			const response: any = await web3BuyItem(item.itemId, totalPrice);
			if (!response?.status) {
				return response;
			} else {
				return fetchAPI({
					endpoint: '/marketplaces/buy',
					method: 'POST',
					data: {
						nft_id: item.nft_id,
						price: response.data.events.MarketItemSold.returnValues.price.toString(),
						web3MarketplaceId: response.data.events.MarketItemSold.returnValues.itemId.toString(),
						priceInSecondaryCurrency:
							response.data.events.MarketItemSold.returnValues.priceInToken.toString(),
						secondaryCurrencyCode:
							response.data.events.MarketItemSold.returnValues.currenyTokenAddress.toString(),
					},
				});
			}
		},
		{
			onSuccess: () => {},
			onError: (err: any) => {
				notify(err?.message, 'error');
			},
		},
	);
};

export const useMakeOffer = () => {
	const queryClient = useQueryClient();

	const { fetchAPI } = useAPICaller();

	const { address } = useWeb3Context();
	const { web3MakeOffer } = useWeb3Marketplace();

	return useMutation(
		async ({ item, offerPrice }: { item: IMarketplaceItemDetail; offerPrice: number }) => {
			const offerPriceInToken = offerPrice * 10 ** 18;

			const response: any = await web3MakeOffer(item.itemId, offerPriceInToken);

			if (!response?.status) {
				return response;
			}

			return fetchAPI({
				endpoint: '/marketplaces/create-offer',
				method: 'PUT',
				data: {
					nft_uri: item.nft_id,
					offer_price_in_token: convertBigNumber(offerPriceInToken, 4),
					web3_marketplace_offer_id:
						response?.data?.events?.AddOffer?.returnValues?.offerId?.toString(),
					token_address: item.tokenId.toString(),
					wallet_address: address,
				},
			});
		},
		{
			onSuccess: () => {
				queryClient.invalidateQueries('getListOffer');
			},
			onError: (err: any) => {
				notify(err?.message, 'error');
			},
		},
	);
};

export const useCancelOffer = () => {
	const queryClient = useQueryClient();

	const { fetchAPI } = useAPICaller();

	const { web3CancelOffer } = useWeb3Marketplace();

	return useMutation(
		async ({ id, offerId }: { id: any; offerId: any }) => {
			const response: any = await web3CancelOffer(offerId);
			if (!response?.status) {
				return response;
			}

			return fetchAPI({
				endpoint: '/marketplaces/withdraw',
				method: 'PUT',
				data: {
					marketplace_offer_id: id,
				},
			});
		},
		{
			onSuccess: () => {
				queryClient.invalidateQueries('getListOffer');
			},
			onError: (err: any) => {
				notify(err?.message, 'error');
			},
		},
	);
};

export const useAcceptOffer = () => {
	const queryClient = useQueryClient();

	const { fetchAPI } = useAPICaller();

	const { web3AcceptOffer } = useWeb3Marketplace();

	return useMutation(
		async ({ id, offerId, nft_id }: { id: any; offerId: any; nft_id: any }) => {
			const response: any = await web3AcceptOffer(offerId);
			if (!response?.status) {
				return response;
			}

			return fetchAPI({
				endpoint: '/marketplaces/accept-offer',
				method: 'PUT',
				data: {
					nft_uri: nft_id,
					marketplace_offer_id: id,
				},
			});
		},
		{
			onSuccess: () => {
				queryClient.invalidateQueries('getListOffer');
			},
			onError: (err: any) => {
				notify(err?.message, 'error');
			},
		},
	);
};
